import React, { useContext, useEffect, useState } from "react";
import { CssBaseline } from '@mui/material';
import _ from "lodash";
import { isBasicMode } from 'config/AppMode';
import LightLayout from 'containers/light/LightLayout';
import EAAppBar from "./EAAppBar";
import EADrawer from "./EADrawer";
import EAScrollToTop from "./common/EASrollToTop";
import { useLocation } from "react-router";
import { Context, initialContextState } from "../states/Store";
import { useAccounts } from "./common/hooks/useAccounts";
import useStyles from "style/js-style/components/LayoutStyle";
import EASnackBar from "./EASnackBar";
import { RoleBasedAccessControlContext } from "services/common/RolesUtils";
import {
  getListActivity,
  getListLegalCategoryTranslation,
  getAllTypeDoc,
  getReferenceData,
} from 'services/edge/ListService';
import { ERROR } from "services/common/Constants";
import { useAuth } from "@ea-webapp/auth";
import i18next from 'i18next';
import { getContextModel } from "services/edge/ContexteService";
import { getAvailableReports } from "services/edge/ReportService";
import ROUTES, { getUrlWithAccountId } from '../config/Routes';
import { useHistory } from 'react-router-dom';
import HasNotPrivilegePage from "./HasNotPrivilegePage";

function Layout({ children, variant, crumbs, isAuthorized = true }) {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const { authState } = useAuth();
  const history = useHistory();
  const [fullCrumbs, setFullCrumbs] = useState();
  const [shownCrumbs, setShownCrumbs] = useState();
  const [currentPath, setCurrentPath] = useState("");
  let location = useLocation();
  const { isGlobalAdmin } = useContext(RoleBasedAccessControlContext);

  useAccounts();

  useEffect(() => {
    if (crumbs) {
      let newCrumbs = [...crumbs.data]
      if (state.crumbsData) {
        if (state.crumbsData.lastCrumbs) {
          newCrumbs.push(...state.crumbsData.lastCrumbs)
        }
      }
      if (!crumbs.hasAdditionalData || (crumbs.hasAdditionalData && state.crumbsData)) {
        setFullCrumbs(newCrumbs);
        setShownCrumbs(newCrumbs.filter(x => !x.hidden));
      }
    }
  }, [crumbs, state.crumbsData])

  useEffect(() => {
    if (state.account && state.account.isSuspended) {
      history.push(getUrlWithAccountId(state.account.id, ROUTES.ACCOUNT_SUSPENDED));
    }
  },[state.account, history, location]);

  // Fetch docType translation
  useEffect(() => {
    const getTypeDoc = async (dispatch) => {
      try {
        const response = await getAllTypeDoc(dispatch, state.account.id, i18next.language);
        if (response?.content) {
          dispatch({ type: "DOCTYPE_TRADUCTIONS", data: response.content })
        }
      } catch (e) {
        dispatch({
          type: 'ALERT',
          alert: { type: ERROR, msg: e.message
          } });
      }
    }

    if (state.account && i18next.language){
      getTypeDoc(dispatch);
    }
  },[state.account, dispatch]);

  // Fetch legal Category translation
  useEffect(() => {
    const getLegalCategoryTranslation= async (accountId, dispatch) => {
      try {
        const response = await getListLegalCategoryTranslation(accountId, dispatch, state.lang);
        if (response?.content) {
          dispatch({ type: "LEGAL_CATEGORY_TRADUCTIONS", data: response.content })
        }
      } catch (e) {
        dispatch({
          type: 'ALERT',
          alert: {
            type: ERROR, msg: e.message
          }
        });
      }
    }

    if (state.account && state.lang) {
      getLegalCategoryTranslation(state.account.id, dispatch);
    }
  }, [state.account, dispatch, state.lang]);

  // Fetch activities translation
  useEffect(() => {
    const getListActivities = async (accountId, dispatch) => {
      try {
        const response = await getListActivity(null, i18next.language, dispatch );
        if (response?.content) {
          dispatch({ type: "ACTIVITY_TRADUCTIONS", data: response.content })
        }
      } catch (e) {
        dispatch({
          type: 'ALERT',
          alert: {
            type: ERROR, msg: e.message
          }
        });
      }
    }

    if (state.account && i18next.language) {
      getListActivities(state.account.id, dispatch);
    }
  }, [state.account, dispatch]);

  // Fetch Reference Data
  useEffect(() => {
    async function getRefData() {
      try {
        const response = await getReferenceData(dispatch);

        if (response?.content) {
          dispatch({ type: "REFERENCE_DATA", data: response.content })
        }
      } catch (e) {
        dispatch({
          type: "ALERT",
          alert: { type: ERROR, msg: "INTERNAL_ERROR" },
        });
      }
    }
    if (authState.isAuthDone) {
      getRefData();
    }
  }, [dispatch, authState.isAuthDone]);

  // Fetch Contexts Data to display or not the menu option
  useEffect(() => {
    async function fetchContextsModels() {
      try {
        await getContextModel(state.account.id, dispatch);
      } catch (ignored) {}
    }
    if (authState.isAuthDone) {
      fetchContextsModels();
    }
  }, [dispatch, state.account, authState.isAuthDone]);

  // Fetch Available Reports to display or not the menu option
  useEffect(() => {
    async function fetchAvailableReports() {
      try {
        await getAvailableReports(state.account.id, dispatch);
      } catch (ignored) {}
    }
    if (authState.isAuthDone && state.account) {
      fetchAvailableReports();
    }
  }, [dispatch, state.account, authState.isAuthDone]);

  useEffect(() => {
    if (location.pathname !== currentPath) {
      dispatch({ type: "ALERT", alert: null });
      dispatch({ type: "CRUMBS", crumbsData: null });
      dispatch({ type: "PAGINATION", pagination: initialContextState.pagination });
      dispatch({ type: "CONTEXT_PAGINATION", context_pagination: initialContextState.context_pagination });
      setCurrentPath(location.pathname);
    }
  }, [location, currentPath, dispatch])


  if (isBasicMode()) {
    return <LightLayout>{children}</LightLayout>;
  }

  return (
    <div className={variant === "list" ? classes.rootList : classes.root}>
      <CssBaseline />
      {state.account && (isGlobalAdmin || !_.isEmpty(state.roles)) && (
        <>
          {!["withoutAppBar", "embeddable"].includes(variant) && <EAAppBar crumbs={shownCrumbs} />}
          {variant !== "embeddable" && <EADrawer crumbs={fullCrumbs} />}
          <div
            className={
              state.drawerIsOpened
                ? state.personalization.isCustomTemplate && variant === "list"
                  ? classes.largeDrawerContentList
                  : variant === "noPadding"
                  ? classes.largeDrawerContentNoPadding
                  : variant === "embeddable"
                  ? classes.largeDrawerContentNoPaddingEmbeddable
                  : classes.largeDrawerContent
                : state.personalization.isCustomTemplate && variant === "list"
                ? classes.contentList
                : variant === "noPadding"
                ? classes.contentNoPadding
                : classes.content
            }
            style={{
              backgroundColor: variant === "grey" ? "#F9F9FB" : "",
            }}
          >
            {!["withoutAppBar", "embeddable"].includes(variant) && <div className={classes.toolbar} />}

            {isAuthorized &&
                <div className="childSection">{children}</div>
            }
            {!isAuthorized &&
              <div className="childSection"><HasNotPrivilegePage /></div>
            }
            <EAScrollToTop />
            <EASnackBar />
          </div>
        </>
      )}
    </div>
  );
}

export default Layout;
